import React from "react"
import { trace, SpanStatusCode } from "@opentelemetry/api"
import logger from "../../../Logger"

const boundaryLogger = logger.child({ module: "ErrorBoundary" })

/**
 * React Error Boundary that catches unhandled component-tree errors and:
 *  1. Records the exception on the active OTel span (visible in distributed traces / Sentry).
 *  2. Logs the error with trace_id / span_id correlation via the Pino logger.
 *  3. Renders a fallback UI so the rest of the app remains usable.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomePage />
 *   </ErrorBoundary>
 *
 * Custom fallback:
 *   <ErrorBoundary fallback={<p>Something went wrong.</p>}>
 *     <SomePage />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Record on the active OTel span so the error appears in the trace and flows to Sentry
    // via the OTel Collector pipeline.
    try {
      const span = trace.getActiveSpan()
      if (span) {
        span.recordException(error)
        span.setStatus({ code: SpanStatusCode.ERROR, message: error?.message })
        span.setAttributes({
          "error.type": error?.name ?? "UnknownError",
          "error.component_stack": info?.componentStack ?? "",
        })
      }
    } catch {
      // Never let instrumentation crash the error boundary itself
    }

    boundaryLogger.error(
      { error, componentStack: info?.componentStack },
      "Unhandled React component error caught by ErrorBoundary"
    )
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
